// Certificates and CMS signatures with WebCrypto: a self-signed signing identity, a detached CMS SignedData over some bytes
// (what a PDF's adbe.pkcs7.detached signature holds), and the check of one (RFC 5280, RFC 5652).

import { bitString, bool, concat, ctx, dateOf, ia5, int, nul, octets, oid, oidOf, parse, seq, set, setOf, stringOf, time, tlv, utf8, type Node } from "./der";

const OID = {
    sha1: "1.3.14.3.2.26", sha256: "2.16.840.1.101.3.4.2.1", sha384: "2.16.840.1.101.3.4.2.2", sha512: "2.16.840.1.101.3.4.2.3",
    rsaEncryption: "1.2.840.113549.1.1.1", sha256WithRsa: "1.2.840.113549.1.1.11", ecPublicKey: "1.2.840.10045.2.1",
    data: "1.2.840.113549.1.7.1", signedData: "1.2.840.113549.1.7.2", contentType: "1.2.840.113549.1.9.3", messageDigest: "1.2.840.113549.1.9.4", signingTime: "1.2.840.113549.1.9.5",
    commonName: "2.5.4.3", organization: "2.5.4.10", email: "1.2.840.113549.1.9.1", basicConstraints: "2.5.29.19", keyUsage: "2.5.29.15", subjectKeyId: "2.5.29.14"
};
const HASH_BY_OID: Record<string, string> = { [OID.sha1]: "SHA-1", [OID.sha256]: "SHA-256", [OID.sha384]: "SHA-384", [OID.sha512]: "SHA-512" };

const subtle = (): SubtleCrypto => { const s = globalThis.crypto?.subtle; if (!s) throw new Error("Signing needs a secure (https) page."); return s; };
const buf = (b: Uint8Array): BufferSource => b as unknown as BufferSource;

export interface SigningIdentity {
    name: string;
    email: string;
    /** The certificate, DER. */
    cert: Uint8Array;
    /** The private key (kept non-extractable). */
    key: CryptoKey;
    created: number;
}

function nameOf(cn: string, email: string, org?: string): Uint8Array {
    const rdn = (o: string, v: Uint8Array) => set(seq(oid(o), v));
    return seq(...(org ? [rdn(OID.organization, utf8(org))] : []), rdn(OID.commonName, utf8(cn)), ...(email ? [rdn(OID.email, ia5(email))] : []));
}

/** Makes a new RSA-2048 key and a certificate for it, signed by itself, valid for two years. */
export async function createIdentity(name: string, email: string): Promise<SigningIdentity> {
    const pair = await subtle().generateKey({ name: "RSASSA-PKCS1-v1_5", modulusLength: 2048, publicExponent: Uint8Array.of(1, 0, 1), hash: "SHA-256" }, false, ["sign", "verify"]) as CryptoKeyPair;
    const spki = new Uint8Array(await subtle().exportKey("spki", pair.publicKey));
    const serial = crypto.getRandomValues(new Uint8Array(16));
    serial[0]! &= 0x7f;
    const notBefore = new Date(Date.now() - 5 * 60_000), notAfter = new Date(Date.now() + 2 * 365 * 86_400_000);
    const subject = nameOf(name, email);
    const sigAlg = seq(oid(OID.sha256WithRsa), nul());
    const ext = (id: string, critical: boolean, value: Uint8Array) => seq(oid(id), ...(critical ? [bool(true)] : []), octets(value));
    const tbs = seq(
        ctx(0, int(2)), int(serial), sigAlg, subject, seq(time(notBefore), time(notAfter)), subject, concat(spki),
        ctx(3, seq(
            ext(OID.basicConstraints, true, seq()),
            ext(OID.keyUsage, true, bitString(Uint8Array.of(0xc0), 6)) // digitalSignature + nonRepudiation
        ))
    );
    const sig = new Uint8Array(await subtle().sign("RSASSA-PKCS1-v1_5", pair.privateKey, buf(tbs)));
    const cert = seq(tbs, sigAlg, bitString(sig));
    return { name, email, cert, key: pair.privateKey, created: Date.now() };
}

const digest = async (alg: string, data: Uint8Array) => new Uint8Array(await subtle().digest(alg, buf(data)));

/** A detached CMS SignedData (DER) over `content`, signed with the identity: what goes into a PDF signature's /Contents. */
export async function signDetached(content: Uint8Array, id: SigningIdentity, signingTime = new Date()): Promise<Uint8Array> {
    const certNode = parse(id.cert)[0]!;
    const tbs = certNode.kids![0]!;
    // version [0] may be absent; the issuer is the 4th element after it
    const k = tbs.kids!;
    const off = k[0]!.tag === 0xa0 ? 1 : 0;
    const serial = k[off]!, issuer = k[off + 2]!;
    const attrs = [
        seq(oid(OID.contentType), setOf(oid(OID.data))),
        seq(oid(OID.messageDigest), setOf(octets(await digest("SHA-256", content)))),
        seq(oid(OID.signingTime), setOf(time(signingTime)))
    ];
    const attrsDer = setOf(...attrs); // what is signed: the attributes as a SET
    const signature = new Uint8Array(await subtle().sign("RSASSA-PKCS1-v1_5", id.key, buf(attrsDer)));
    const signedAttrs = tlv(0xa0, parse(attrsDer)[0]!.body); // the same attributes, tagged [0] inside the SignerInfo
    const signerInfo = seq(
        int(1), seq(issuer.raw, serial.raw), seq(oid(OID.sha256), nul()), signedAttrs,
        seq(oid(OID.rsaEncryption), nul()), octets(signature)
    );
    const signedData = seq(int(1), set(seq(oid(OID.sha256), nul())), seq(oid(OID.data)), ctx(0, id.cert), set(signerInfo));
    return seq(oid(OID.signedData), ctx(0, signedData));
}

export interface SignatureCheck {
    /** The signer's name from the certificate. */
    signer: string;
    email: string;
    /** When the signer said they signed (a claim in the signature itself). */
    signedAt: Date | null;
    /** The signed bytes are the ones the signature was made over. */
    intact: boolean;
    /** The signature itself checks out against the certificate's key. */
    signatureValid: boolean;
    /** The certificate names itself as its own issuer. */
    selfSigned: boolean;
    notBefore: Date | null;
    notAfter: Date | null;
    problem: string | null;
}

function subjectOf(cert: Node): { cn: string; email: string; org: string } {
    const tbs = cert.kids![0]!, k = tbs.kids!, off = k[0]!.tag === 0xa0 ? 1 : 0;
    const subject = k[off + 4]!;
    let cn = "", email = "", org = "";
    for (const rdn of subject.kids ?? []) for (const atv of rdn.kids ?? []) {
        const [t, v] = atv.kids ?? [];
        if (!t || !v) continue;
        const o = oidOf(t);
        if (o === OID.commonName) cn = stringOf(v);
        else if (o === OID.email) email = stringOf(v);
        else if (o === OID.organization) org = stringOf(v);
    }
    return { cn: cn || org, email, org };
}

/** Checks one detached CMS signature over `signed` (the PDF bytes its ByteRange covers). */
export async function verifyDetached(cmsDer: Uint8Array, signed: Uint8Array): Promise<SignatureCheck> {
    const base: SignatureCheck = { signer: "", email: "", signedAt: null, intact: false, signatureValid: false, selfSigned: false, notBefore: null, notAfter: null, problem: null };
    try {
        const ci = parse(cmsDer)[0]!;
        if (oidOf(ci.kids![0]!) !== OID.signedData) return { ...base, problem: "This is not a CMS signature." };
        const sd = ci.kids![1]!.kids![0]!;
        const parts = sd.kids!;
        const certsNode = parts.find((p) => p.tag === 0xa0);
        const sis = parts[parts.length - 1]!;
        const si = sis.kids![0]!;
        const sik = si.kids!;
        // signerInfo: version, sid, digestAlg, [0] signedAttrs?, sigAlg, signature
        const sid = sik[1]!;
        const digestAlg = HASH_BY_OID[oidOf(sik[2]!.kids![0]!)] ?? "SHA-256";
        const hasAttrs = sik[3]!.tag === 0xa0;
        const attrsNode = hasAttrs ? sik[3]! : null;
        const sigAlgOid = oidOf(sik[hasAttrs ? 4 : 3]!.kids![0]!);
        const signature = sik[hasAttrs ? 5 : 4]!.body;
        // the signer's certificate: the one whose serial and issuer match
        const certs = certsNode?.kids ?? [];
        const serialRaw = sid.kids ? sid.kids[1]!.body : null;
        const cert = certs.find((c) => { const k = c.kids![0]!.kids!; const off = k[0]!.tag === 0xa0 ? 1 : 0; return serialRaw ? bytesEqual(k[off]!.body, serialRaw) : true; }) ?? certs[0];
        if (!cert) return { ...base, problem: "The signature has no certificate." };
        const who = subjectOf(cert);
        const k = cert.kids![0]!.kids!, off = k[0]!.tag === 0xa0 ? 1 : 0;
        const validity = k[off + 3]!.kids!;
        const out: SignatureCheck = { ...base, signer: who.cn, email: who.email, selfSigned: bytesEqual(k[off + 2]!.raw, k[off + 4]!.raw), notBefore: dateOf(validity[0]!), notAfter: dateOf(validity[1]!) };
        // 1. the signed bytes hash to the digest the signature carries
        const want = await digest(digestAlg, signed);
        let signedBytes: Uint8Array = signed;
        if (attrsNode) {
            let md: Uint8Array | null = null;
            for (const a of attrsNode.kids ?? []) {
                const id = oidOf(a.kids![0]!);
                if (id === OID.messageDigest) md = a.kids![1]!.kids![0]!.body;
                if (id === OID.signingTime) out.signedAt = dateOf(a.kids![1]!.kids![0]!);
            }
            out.intact = md !== null && bytesEqual(md, want);
            // 2. the signature is over the attributes as a SET (tag 0x31)
            signedBytes = concat(Uint8Array.of(0x31), attrsNode.raw.subarray(1));
        } else out.intact = true;
        // 3. the signature checks out against the certificate's public key
        const spki = k[off + 5]!.raw;
        const keyAlg = oidOf(k[off + 5]!.kids![0]!.kids![0]!);
        if (keyAlg === OID.rsaEncryption) {
            const key = await subtle().importKey("spki", buf(spki), { name: "RSASSA-PKCS1-v1_5", hash: digestAlg }, false, ["verify"]);
            out.signatureValid = await subtle().verify("RSASSA-PKCS1-v1_5", key, buf(signature), buf(signedBytes));
        } else if (keyAlg === OID.ecPublicKey) {
            const curve = oidOf(k[off + 5]!.kids![0]!.kids![1]!) === "1.3.132.0.34" ? "P-384" : oidOf(k[off + 5]!.kids![0]!.kids![1]!) === "1.3.132.0.35" ? "P-521" : "P-256";
            const key = await subtle().importKey("spki", buf(spki), { name: "ECDSA", namedCurve: curve }, false, ["verify"]);
            const size = curve === "P-256" ? 32 : curve === "P-384" ? 48 : 66;
            const rs = parse(signature)[0]!.kids!;
            const fix = (n: Node) => { const b = n.body[0] === 0 ? n.body.subarray(1) : n.body; const o = new Uint8Array(size); o.set(b, size - b.length); return o; };
            out.signatureValid = await subtle().verify({ name: "ECDSA", hash: digestAlg }, key, buf(concat(fix(rs[0]!), fix(rs[1]!))), buf(signedBytes));
        } else out.problem = `Unsupported key type (${keyAlg}).`;
        void sigAlgOid;
        return out;
    } catch (e) {
        return { ...base, problem: e instanceof Error ? e.message : String(e) };
    }
}

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}
